import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { foldJsonInMarkdown, promoteBoldLinesToHeadings } from '../lib/jsonFold';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

export default function ClientPortalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: d } = await axios.get(`${BACKEND}/api/portal/${token}`);
        if (!cancelled) {
          setData(d);
          setErr(null);
        }
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || e.message || 'Unable to load portal');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-200">
        <p className="text-sm">Loading client portal…</p>
      </div>
    );
  }
  if (err || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-200 px-6">
        <p className="text-sm text-center text-red-300">{err || 'Not found'}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 px-4 py-10">
      <div className="max-w-3xl mx-auto space-y-8">
        <header className="border-b border-slate-800 pb-6">
          <p className="text-xs uppercase tracking-widest text-emerald-400 mb-1">Client portal</p>
          <h1 className="text-2xl font-semibold">{data.client_name}</h1>
          <p className="text-sm text-slate-400 mt-1">
            {data.risk_bucket ? `Risk: ${data.risk_bucket}` : ''}
            {data.corpus ? ` · Corpus ~₹${Number(data.corpus).toLocaleString('en-IN')}` : ''}
          </p>
        </header>

        <section>
          <h2 className="text-sm font-semibold text-emerald-400 mb-2">Holdings</h2>
          {Array.isArray(data.holdings) && data.holdings.length > 0 ? (
            <ul className="text-sm space-y-1 text-slate-300">
              {data.holdings.slice(0, 40).map((h, i) => (
                <li key={i}>
                  {h.symbol || h.nseSymbol} — qty {h.quantity ?? '—'}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500">No holdings synced for this view.</p>
          )}
        </section>

        {data.latest_proposal && (
          <section>
            <h2 className="text-sm font-semibold text-emerald-400 mb-2">Latest approved proposal</h2>
            <p className="text-sm text-slate-300 whitespace-pre-wrap">
              {data.latest_proposal.proposal?.executive_summary || 'Proposal on file.'}
            </p>
          </section>
        )}

        {data.latest_review && (
          <section>
            <h2 className="text-sm font-semibold text-emerald-400 mb-2">Review — {data.latest_review.period}</h2>
            <div className="prose prose-sm prose-invert max-w-none break-words leading-relaxed
                            prose-h2:text-emerald-300 prose-h3:text-slate-400 prose-h3:uppercase prose-h3:tracking-wide
                            prose-p:my-3 prose-ul:my-3 prose-ul:pl-5 prose-li:my-1
                            prose-strong:text-slate-100
                            prose-pre:max-w-full prose-pre:overflow-x-auto prose-pre:whitespace-pre-wrap
                            prose-pre:break-words prose-pre:text-xs">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {promoteBoldLinesToHeadings(foldJsonInMarkdown(data.latest_review.content_md || ''))}
              </ReactMarkdown>
            </div>
          </section>
        )}

        <p className="text-xs text-slate-600 pt-8">
          {data.performance_summary?.message}
        </p>
      </div>
    </div>
  );
}
